import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const TicketThread = ({ ticket, onChange, admin }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (fn) => {
    setBusy(true);
    try { onChange((await fn()).data); setText(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ticket-thread" className="lux-card space-y-4 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xl text-amber-50" data-testid="ticket-thread-subject">{ticket.subject}</h3>
        <StatusBadge status={ticket.status === "open" ? "pending" : "completed"} testId="ticket-thread-status" />
      </div>
      <p className="text-xs text-zinc-500">{ticket.user_name} · {ticket.user_role}{ticket.trip_id && ` · trip #${ticket.trip_id.slice(-6)}`}</p>
      <div className="space-y-3">
        {ticket.messages.map((m, i) => (
          <div key={i} data-testid="ticket-message" className={`rounded-xl px-4 py-3 ${m.author_role === "admin" ? "border border-amber-400/30 bg-amber-400/5" : "bg-white/[0.03]"}`}>
            <p className="text-xs font-semibold text-amber-300">{m.author_name}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-200">{m.text}</p>
            <p className="mt-1 text-[10px] text-zinc-500">{fmtDate(m.created_at)}</p>
          </div>
        ))}
      </div>
      {ticket.status === "open" && (
        <div className="space-y-3">
          <textarea data-testid="ticket-reply-input" className="lux-input min-h-[80px]" placeholder="Write a reply…" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button data-testid="ticket-reply-button" disabled={busy || !text.trim()} onClick={() => act(() => api.post(`/tickets/${ticket.id}/reply`, { text }))} className="btn-gold !py-2.5">Send reply</button>
            {admin && <button data-testid="ticket-close-button" disabled={busy} onClick={() => act(() => api.post(`/tickets/${ticket.id}/close`))} className="btn-outline-gold !py-2.5">Close ticket</button>}
          </div>
        </div>
      )}
    </div>
  );
};

export const TicketList = ({ tickets, selected, onSelect }) => (
  <div className="lux-card divide-y divide-white/5" data-testid="ticket-list">
    {tickets.length === 0 && <p className="p-6 text-sm text-zinc-500">No tickets yet.</p>}
    {tickets.map((t) => (
      <button key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => onSelect(t)} className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-amber-400/5 ${selected === t.id ? "bg-amber-400/5" : ""}`}>
        <span className="min-w-0"><span className="block truncate text-sm text-amber-100">{t.subject}</span><span className="text-xs text-zinc-500">{t.user_name} · {fmtDate(t.updated_at)}</span></span>
        <span data-testid={`ticket-status-${t.id}`} className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs ${t.status === "open" ? "border-amber-500/40 text-amber-300" : "border-zinc-600 text-zinc-400"}`}>{t.status === "open" ? "Open" : "Closed"}</span>
      </button>
    ))}
  </div>
);

const NewTicket = ({ trips, onCreated }) => {
  const [form, setForm] = useState({ subject: "", message: "", trip_id: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", { ...form, trip_id: form.trip_id || null });
      setForm({ subject: "", message: "", trip_id: "" });
      onCreated(data);
      toast.success("Ticket submitted");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate className="lux-card space-y-4 p-5 sm:p-6" data-testid="new-ticket-form">
      <p className="lux-eyebrow">New request</p>
      <input data-testid="ticket-subject-input" className="lux-input" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
      <textarea data-testid="ticket-message-input" className="lux-input min-h-[110px]" placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <select data-testid="ticket-trip-select" className="lux-input" value={form.trip_id} onChange={(e) => setForm({ ...form, trip_id: e.target.value })}>
        <option value="">No trip (general)</option>
        {trips.map((t) => <option key={t.id} value={t.id}>{fmtDate(t.timestamps?.created_at)} · {t.pickup.address.slice(0, 40)}</option>)}
      </select>
      {error && <p data-testid="ticket-form-error" className="text-sm text-red-300">{error}</p>}
      <button type="submit" disabled={busy} data-testid="ticket-submit-button" className="btn-gold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Submit</button>
    </form>
  );
};

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [trips, setTrips] = useState([]);
  const [current, setCurrent] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/tickets");
    setTickets(data);
    setCurrent((c) => (c ? data.find((t) => t.id === c.id) || c : c));
  }, []);
  useEffect(() => { load(); api.get("/trips").then(({ data }) => setTrips(data)); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const upsert = (t) => { setCurrent(t); load(); };
  return (
    <PortalShell>
      <p className="lux-eyebrow">{user.role === "driver" ? "Driver support" : "Customer service"}</p>
      <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Customer Service</h1>
      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr]" data-testid="support-page">
        <div className="space-y-6">
          <NewTicket trips={trips} onCreated={upsert} />
          <TicketList tickets={tickets} selected={current?.id} onSelect={setCurrent} />
        </div>
        <div>{current ? <TicketThread ticket={current} onChange={upsert} /> : <p className="lux-card p-8 text-sm text-zinc-500">Select a ticket to view the conversation.</p>}</div>
      </div>
    </PortalShell>
  );
}
